import { Button, Col, ConfigProvider, Form, Input, Row } from 'antd';
import {
  createOrganization,
  getUserOrganizationAccess,
  joinOgranization,
  Organization,
  OrganizationAccess,
  UserOrganizationAccess,
  UserOrganizationInvite,
  verifyUserInvitation,
} from '../../api/organization';
import {
  AccessTokenUser,
  OrganizationAccessRoles,
  getUserFromAccessToken,
  getUsersAccessibleOrganizations,
  logoutUser,
  setCurrentlyActiveOrganization,
  setUsersOrganizationAccess,
} from '../../common/auth';
import RoutePaths from '../../router/RoutePaths';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './SelectOrganization.scss';
import './AuthPage.scss';
import { useGetOrganizationAccessesAndInvitesWithOrganization } from '../../serverState/organization';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import { queryClient } from '../../queryClient';
import CompanyLogoSvg from '../../assets/icons/company-logo.svg?react';
import colorTokens from '../../constants/design_system/colors';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';

export default function SelectOrganization() {
  const allUserOrgAcessesQuery = useGetOrganizationAccessesAndInvitesWithOrganization();

  const navigate = useNavigate();
  const [accessibleOrgs, setAccessibleOrgs] = useState<UserOrganizationAccess[]>([]);
  const [user, setUser] = useState<AccessTokenUser | undefined>(undefined);
  const [initOnboardingForm] = Form.useForm();
  const selectOrganization = async (selectedOrgWithAccess: UserOrganizationAccess) => {
    const userOrganizationAccessRes = await getUserOrganizationAccess();

    await setUsersOrganizationAccess(userOrganizationAccessRes.data);

    await setCurrentlyActiveOrganization(selectedOrgWithAccess);

    navigate(RoutePaths.Home);
  };

  const acceptInviteToOrganization = async (selectedOrgWithInvite: UserOrganizationInvite) => {
    await verifyUserInvitation(selectedOrgWithInvite.id, selectedOrgWithInvite.UserInvitation[0].id);

    const userOrganizationAccessRes = await getUserOrganizationAccess();

    await setUsersOrganizationAccess(userOrganizationAccessRes.data);

    await setCurrentlyActiveOrganization(
      userOrganizationAccessRes.data.find((org) => org.id === selectedOrgWithInvite.id)!
    );

    navigate(RoutePaths.Home);
  };

  const joinOrganization = async (selectedOrg: Organization) => {
    await joinOgranization(selectedOrg.id);

    const userOrganizationAccessRes = await getUserOrganizationAccess();

    await setUsersOrganizationAccess(userOrganizationAccessRes.data);

    await setCurrentlyActiveOrganization(userOrganizationAccessRes.data.find((org) => org.id === selectedOrg.id)!);

    navigate(RoutePaths.Home);
  };

  useEffect(() => {
    getUsersAccessibleOrganizations()
      .then((res) => {
        return setAccessibleOrgs(res);
      })
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    getUserFromAccessToken()
      .then((res) => {
        return setUser(res);
      })
      .catch((e) => console.log(e));
  }, []);

  interface onFinishStructure {
    organizationName: string;
  }

  const onFinish = async (values: onFinishStructure) => {
    const newOrgRes = await createOrganization(values.organizationName);

    await Promise.all([
      // Updating all the accessible orgs
      setUsersOrganizationAccess(newOrgRes.data.organizationsAccess),

      // Setting users currently active org to the newly created organization
      // TODO: Update API to return a different model here.
      setCurrentlyActiveOrganization(newOrgRes.data.createdOrganization),
    ]);
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['usersOrganizationAccessAndInvites'],
        }),
        // BaseIndex mirrors fresh firmographics (e.g. self-sort submission) from this
        // query into activeOrganization — without refetching it here, a newly created
        // org keeps stale data and the self-sort banner never hides after submission.
        queryClient.refetchQueries({ queryKey: ['usersOrganizationAccess'] }),
      ]);

      analytics.track(AnalyticsEvent.organizationCreated, {
        organizationId: newOrgRes.data.createdOrganization.id,
      });

      setTimeout(() => {
        navigate(`/${newOrgRes.data.createdOrganization.publicId}/new-org-invite`);
      }, 200);
    } catch (e) {
      console.log(e);
    }
  };

  const CreateNewOrganizationRow = () => {
    return (
      <div className="flex flex-col justify-center items-center mt-8">
        <h1 className="text-left text-brand-grey-dark font-semibold mb-7">Create New Organization</h1>

        <Form
          form={initOnboardingForm}
          name="initial-onboarding"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark="optional"
          className="w-[320px]"
        >
          <Form.Item
            label="Organization name"
            name="organizationName"
            rules={[
              {
                required: true,
                message: 'Organization name is required field!',
              },
            ]}
            wrapperCol={{ sm: 24 }}
            className="mb-6 text-brand-grey-dark text-xs font-semibold font-figtree leading-none"
          >
            <Input placeholder="Enter your organizations name" size="large" />
          </Form.Item>

          <Form.Item wrapperCol={{ sm: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              shape="round"
              className="onboarding-submit-button font-semibold"
            >
              Create an Organization
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const membersText = (organizationAccesses: OrganizationAccess[]) => {
    let membersNum = '';
    if (organizationAccesses) {
      const adminAccess = organizationAccesses.find((access) => access.roleToAssume === OrganizationAccessRoles.ADMIN);
      if (adminAccess && adminAccess.User && adminAccess.User.email) {
        const restOfMembers = organizationAccesses.length - 1;
        membersNum =
          adminAccess.User.email +
          (restOfMembers > 0 ? ' and ' + restOfMembers + (restOfMembers === 1 ? ' member' : ' members') : '');
      } else {
        membersNum = organizationAccesses.length + (organizationAccesses.length === 1 ? ' member' : ' members');
      }
    }
    return membersNum;
  };

  const OrganizationList = () => {
    const userOrgAcesses =
      allUserOrgAcessesQuery.data?.organizationsAccess?.filter((org) => org.deletedAt === null) || [];
    const userOrgInvites = allUserOrgAcessesQuery.data?.organizationsInvite || [];
    const userOrgsToJoin = allUserOrgAcessesQuery.data?.organizationsToJoin || [];
    if (userOrgAcesses.length === 0 && userOrgInvites.length === 0 && userOrgsToJoin.length === 0) {
      return <>{CreateNewOrganizationRow()}</>;
    } else {
      const listOfOrgsToJoin =
        userOrgsToJoin.map((organization) => {
          const org = organization as UserOrganizationAccess;
          const membersNum = membersText(org?.OrganizationAccess);
          const joinActionTitle = 'Join';
          return OrganizationRow(org, membersNum, joinActionTitle, joinOrganization);
        }) || [];

      const listOfOrgs =
        userOrgAcesses.map((org) => {
          const membersNum = membersText(org?.OrganizationAccess);
          const joinActionTitle = 'Open';
          return OrganizationRow(org, membersNum, joinActionTitle, selectOrganization);
        }) || [];

      const listOfInvites =
        userOrgInvites.map((org) => {
          const membersNum = membersText(org?.OrganizationAccess);
          const joinActionTitle = 'Accept invitation';
          return OrganizationRow(org, membersNum, joinActionTitle, acceptInviteToOrganization);
        }) || [];

      return (
        <>
          <h1 className="text-center font-semibold mt-12 mb-8">Join Organization</h1>
          <div className="flex items-center justify-center ">
            <div className="w-[482px]">
              <div className="text-neutral-900 text-sm font-normal font-figtree mb-3 mx-0.5">
                Please log in, accept the invitation, or join the organization to continue.
              </div>
              {listOfInvites}
              {listOfOrgs}
              {listOfOrgsToJoin}
            </div>
          </div>

          <div className="text-center text-neutral-900 text-sm mt-6">Or</div>
          {CreateNewOrganizationRow()}
        </>
      );
    }
  };

  const OrganizationSelectionHeader = () => {
    return (
      <>
        <div className="flex align-middle justify-center">
          <LogoFullIcon />
        </div>
        <div className="flex align-middle justify-center">
          <div className="w-96 text-center mt-6">
            <span className="text-neutral-900 text-sm font-normal font-figtree">Joining as </span>
            <span className="text-brand-grey-dark text-sm font-medium font-figtree">{user?.user.email}</span>
            <span
              className="text-neutral-900 text-sm font-normal font-figtree ml-4 cursor-pointer"
              onClick={logoutUser}
            >
              Change
            </span>
          </div>
        </div>
      </>
    );
  };

  const OrganizationRow = (
    org: Organization,
    membersNum: string,
    joinActionTitle: string,
    selectOrganizationAction: (organization: Organization) => Void
  ) => {
    return (
      <Row justify="center" align="middle" className="org-select-row" key={org.id}>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorPrimary: colorTokens.blue[600],
              },
            },
          }}
        >
          <Col span={24}>
            <div className="organization-selection-block">
              <span className="flex items-center justify-center w-10 h-10 bg-neutral-100 rounded border border-neutral-100 mx-2.5">
                <CompanyLogoSvg className="w-5 h-5" />
              </span>
              <div className="organization_details_body">
                <span className="organization-name">{org.name}</span>
                <span className="text-neutral-900 text-xs font-figtree font-semibold">{membersNum}</span>
              </div>
              <Button
                type="primary"
                className="bg-blue-600 text-white text-sm font-semibold"
                onClick={() => {
                  selectOrganizationAction(org);
                }}
              >
                {joinActionTitle}
              </Button>
            </div>
          </Col>
        </ConfigProvider>
      </Row>
    );
  };

  return (
    <>
      <div className="mt-16">
        {OrganizationSelectionHeader()}
        {OrganizationList()}
      </div>
    </>
  );
}
